import {
  decodeAudio,
  playBuffer,
  readSetting,
  readUrlSetting,
  soundOn,
  writeSetting,
} from "./audioEngine";

// Opnames in public/sounds/ (gemaakt door tools/geluiden.py), voor geluid met textuur.
// Ze staan in public/ zodat je er een kunt vervangen zonder opnieuw te bouwen.
// Welk bestand speelt kies je in het animatielab, los van de geluidsset.

const folder = `${process.env.PUBLIC_URL || ""}/sounds`;

// In deze volgorde geprobeerd: mp3 werkt overal, ogg is kleiner, wav is groot.
const extensions = ["mp3", "ogg", "wav"];

// Per animatie de keuzes, de eerste is standaard; gain 1 is het bestand zoals het is.
export const sampleOptions = {
  fireworks: {
    label: "Record",
    hint: "grootste verkoop ooit",
    gain: 0.8,
    files: [{ file: "record-triomf", name: "Triomf", note: "klap en een akkoord dat blijft staan" }],
  },
  "sale-splash": {
    label: "Aankondiging",
    hint: "alleen bij een set die hier zelf niets voor heeft",
    gain: 0.9,
    files: [{ file: "verkoop", name: "Opname", note: "klap plus akkoord" }],
  },
  "sale-horn": {
    label: "Toeter",
    hint: "bij het seintje, vlak voor de omroeper",
    // Het bestand zelf is al luid: de helft van de eerdere 1,3.
    gain: 0.65,
    // De download begint met 0,2s stilte; de stoten in animations/horn.js tellen vanaf hier.
    skip: 0.2,
    files: [{ file: "toeter", name: "DJ-toeter", note: "tu-tu-tu-tu-tu-tuuuu, 2,7s" }],
  },
  "sale-attention": {
    label: "Omroeper",
    hint: "na de toeter: \"Attentie! We hebben een nieuwe verkoop!\"",
    // Voorlopig Frank (Windows, nl-NL), door tools/omroeper.py; een eigen opname mag erover.
    // 1,1 zet hem met soundVolume net onder de drempel van de begrenzer.
    gain: 1.1,
    // Valt onder de stemmenknop; uit is alleen de stem weg, de ATTENTIE-band blijft in beeld.
    voice: true,
    files: [{ file: "omroeper", name: "Frank, episch", note: "5s, diep, met trailermuziek" }],
  },
  "sale-charge-epic": {
    label: "Aanloop, episch",
    hint: "na de omroeper, in plaats van de tikken van de set (alleen bij Film)",
    // Trom op elk blokje en de klap op de flits (tools/omroeper.py, tijden uit charge.js).
    gain: 1,
    files: [{ file: "aanloop", name: "Trailer", note: "trom, strijkers omhoog, stil, BOEM" }],
  },
  "sale-charge-huge": {
    label: "Aanloop, grote deal",
    hint: "als de aanloop episch is en de verkoop boven bigDealAmount (constants.js) zit",
    gain: 1.1,
    files: [{ file: "aanloop-groot", name: "Trailer", note: "roffel, twee octaven omhoog, BOEM-BOEM" }],
  },
  "target-charge-epic": {
    label: "Target: meter naar 100, episch",
    hint: "bij Film: trom op elke 5%, \"Het teamtarget... is GEHAALD!\" op de 100",
    gain: 1.1,
    voice: true,
    files: [{ file: "target-aanloop", name: "Trailer", note: "trom, strijkers, stil, BOEM in majeur" }],
  },
  "target-charge-bare": {
    label: "Target: meter naar 100, zonder stem",
    hint: "bij Film, als de stemmen uit staan",
    gain: 1.1,
    files: [{ file: "target-aanloop-kaal", name: "Trailer", note: "trom, strijkers, stil, BOEM in majeur" }],
  },
  "record-build-epic": {
    label: "Record: opbouw, episch",
    hint: "naar de stempel toe, bij Film",
    gain: 1,
    files: [{ file: "record-opbouw", name: "Trailer", note: "trom en strijkers, stil voor de stempel" }],
  },
  "record-voice": {
    label: "Record: stem",
    hint: "op de stempel bij het bordrecord, bij Film; neemt het vuurwerkgeluid over",
    gain: 1.1,
    voice: true,
    files: [{ file: "record-stem", name: "Frank, episch", note: "BOEM, \"Grootste verkoop ooit!\", slot in majeur" }],
  },
  "best-voice": {
    label: "Eigen record: stem",
    hint: "op de stempel bij een eigen record, bij Film; neemt het eigen-recordgeluid over",
    gain: 1.1,
    voice: true,
    files: [{ file: "eigen-record-stem", name: "Frank, episch", note: "BOEM, \"Persoonlijk record!\", slot in majeur" }],
  },
  "sale-confetti": {
    label: "Confetti",
    hint: "elk salvo van de kanonnen bij een verkoop, niet bij het feestje",
    // Tot drie salvo's per verkoop, 3s uit elkaar: zacht houden en het bestand korter dan 3s.
    gain: 0.84,
    files: [{ file: "confetti", name: "Opname", note: "plop en geritsel" }],
  },
  "personal-best": {
    label: "Eigen record",
    hint: "iemands grootste verkoop tot nu toe",
    gain: 0.9,
    files: [{ file: "eigen-record", name: "Opname", note: "twee noten omhoog en een akkoord" }],
  },
  "target-hit": {
    label: "Team target",
    hint: "een keer per maand",
    // Staat op de tv hard: liever iets onder de rest.
    gain: 0.82,
    files: [{ file: "target", name: "Fanfare", note: "koper en een bekkenveeg" }],
  },
  "party-dance": {
    label: "Feest",
    hint: "als iemand het feestje zelf aanzet",
    // Iets zachter: dit loopt een kwart minuut door.
    gain: 0.75,
    // Fades lopen gelijk met het beeld (fx-party-lights-fade in animations.css, en party.js).
    fadeIn: 4.5,
    fadeOut: 3,
    files: [{ file: "feest", name: "Opname", note: "een heel nummer, met fade" }],
  },
  "target-song": {
    label: "Targetfeest",
    hint: "als het team het target haalt",
    gain: 0.75,
    // Hetzelfde nummer, twee keer zo lang; de infade eindigt vlak voor de klap op 3s
    // waar het eerste salvo op afgaat (zie targetPartyGroup in animations/party.js).
    fadeIn: 2.5,
    fadeOut: 3,
    files: [{ file: "feest-target", name: "Opname", note: "31s, met fade" }],
  },
};

// De stemmen met voice: true (de omroeper bij een nieuwe verkoop, records en het target).
// Ze staan standaard uit en kunnen aan in het animatielab of met ?stemmen=aan. Uit telt als
// niet gevonden: dan klinkt wat er anders zou klinken (bij de omroeper: niets, de band blijft).
const voicesKey = "tdVoices";
let voicesChosen = null;

export function extraVoices() {
  if (voicesChosen === null) {
    const saved = readUrlSetting("stemmen", ["aan", "uit"], voicesKey) || readSetting(voicesKey);
    voicesChosen = saved === "aan";
  }
  return voicesChosen;
}

export function setExtraVoices(next) {
  voicesChosen = Boolean(next);
  writeSetting(voicesKey, voicesChosen ? "aan" : "uit");
  if (voicesChosen) warmSamples();
}

const allowed = (id) => !sampleOptions[id]?.voice || extraVoices();

// Bestandsnaam -> AudioBuffer (null als er niets ligt), per bestand één keer opgehaald.
const buffers = new Map();
const loading = new Map();

const storeKey = (id) => `tdSound:${id}`;
const hasFile = (option, file) => option.files.some((entry) => entry.file === file);

// Het gekozen bestand voor deze animatie, anders de eerste uit de lijst.
export function chosenFile(id) {
  const option = sampleOptions[id];
  if (!option) return null;

  const saved = readSetting(storeKey(id));
  return hasFile(option, saved) ? saved : option.files[0].file;
}

async function fetchBuffer(name) {
  for (const extension of extensions) {
    try {
      const response = await fetch(`${folder}/${name}.${extension}`);
      if (!response.ok) continue;
      const decoded = await decodeAudio(await response.arrayBuffer());
      if (decoded) return decoded;
    } catch {
      // Geen netwerk of geen bestand: volgende extensie.
    }
  }
  return null;
}

// Eén bestand ophalen; wie tijdens het laden nogmaals vraagt krijgt dezelfde belofte.
function load(name) {
  if (buffers.has(name)) return Promise.resolve(buffers.get(name));
  if (loading.has(name)) return loading.get(name);

  const promise = fetchBuffer(name).then((buffer) => {
    buffers.set(name, buffer);
    loading.delete(name);
    return buffer;
  });
  loading.set(name, promise);
  return promise;
}

// De gekozen bestanden vooraf ophalen, anders komt het geluid bij de eerste verkoop te laat.
export function warmSamples() {
  if (!soundOn()) return Promise.resolve();
  return Promise.all(
    Object.keys(sampleOptions).filter(allowed).map((id) => load(chosenFile(id))),
  );
}

// Een andere smaak kiezen; de belofte lost op als hij klaar is om te spelen.
export function chooseSample(id, file) {
  const option = sampleOptions[id];
  if (!option || !hasFile(option, file)) return Promise.resolve(null);

  writeSetting(storeKey(id), file);
  return load(file);
}

// Het gekozen bestand afspelen en teruggeven of dat lukte; gain is die van de set.
export function playSample(id, gain = 1) {
  const option = sampleOptions[id];
  if (!option || !allowed(id)) return false;

  const buffer = buffers.get(chosenFile(id));
  if (!buffer) return false;
  return playBuffer(buffer, {
    gain: option.gain * gain,
    fadeIn: option.fadeIn ?? 0,
    fadeOut: option.fadeOut ?? 0,
    skip: option.skip ?? 0,
  });
}

// Of het gekozen bestand voor deze animatie klaarstaat.
export const hasSample = (id) => allowed(id) && Boolean(buffers.get(chosenFile(id)));

// Welke animaties een gevonden bestand hebben, voor het animatielab.
export function foundSamples() {
  return Object.keys(sampleOptions).filter((id) => hasSample(id));
}
